import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppCtaLink from './AppCtaLink';
import { LEGAL_NAME } from '../data/legalEntity';
import { LUMI_INSTAGRAM_PROFILE } from '../data/lumiStories';
import '../styles/_footer.scss';

const Footer = () => {
  const { t } = useTranslation();
  const { language } = useParams();

  const getLocalizedLink = (path) => `/${language}${path === '/' ? '' : path}`;

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-logo">
            <Link to={getLocalizedLink('/')} className="logo">
              <span className="logo-text">AI PSY HELP</span>
            </Link>
            <p className="footer-tagline">{t('footer.tagline')}</p>
            <p className="footer-brand-line">{t('footer.brand_line')}</p>
            <p className="footer-legal-name">{LEGAL_NAME}</p>
            <div className="footer-app-stores">
              <AppCtaLink
                platform="android"
                placement="footer"
                className="footer-app-store-link google-play"
                aria-label={t('home.app_stores.google_play')}
              >
                <img
                  src="/images/google_en.svg"
                  alt={t('home.app_stores.google_play')}
                  className="footer-app-store-badge"
                  width="155"
                  height="46"
                  loading="lazy"
                  decoding="async"
                />
              </AppCtaLink>
              <AppCtaLink
                platform="ios"
                placement="footer"
                className="footer-app-store-link app-store"
                aria-label={t('home.app_stores.app_store')}
              >
                <img
                  src="/images/apple_en.svg"
                  alt={t('home.app_stores.app_store')}
                  className="footer-app-store-badge"
                  width="155"
                  height="46"
                  loading="lazy"
                  decoding="async"
                />
              </AppCtaLink>
            </div>
          </div>

          <div className="footer-links">
            <div className="footer-links-column">
              <h2 className="footer-links-title">{t('footer.navigation_title')}</h2>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/')}>{t('nav.home')}</Link></li>
                <li><Link to={getLocalizedLink('/news')}>{t('nav.news')}</Link></li>
                <li><Link to={getLocalizedLink('/how-lumi-works')}>{t('nav.how_lumi_works')}</Link></li>
                <li><Link to={getLocalizedLink('/lumi-stories')}>{t('nav.lumi_stories')}</Link></li>
                <li><Link to={getLocalizedLink('/download')}>{t('nav.download')}</Link></li>
                <li><Link to={getLocalizedLink('/videos')}>{t('nav.videos')}</Link></li>
                <li><Link to={getLocalizedLink('/team')}>{t('footer.team', 'Team')}</Link></li>
                <li><Link to={getLocalizedLink('/research')}>{t('footer.research', 'Research')}</Link></li>
                <li><Link to={getLocalizedLink('/ai-wellbeing')}>{t('footer.ai_wellbeing', 'AI wellbeing')}</Link></li>
              </ul>
            </div>
            <div className="footer-links-column">
              <h2 className="footer-links-title">{t('footer.solutions_title')}</h2>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/corporate')}>{t('nav.corporate')}</Link></li>
                <li><Link to={getLocalizedLink('/ngo')}>{t('nav.ngo')}</Link></li>
                <li><Link to={getLocalizedLink('/webinars')}>{t('nav.webinars')}</Link></li>
                <li><Link to={getLocalizedLink('/for-specialists')}>{t('nav.for_specialists', 'For specialists')}</Link></li>
                <li><Link to={getLocalizedLink('/security')}>{t('footer.security', 'Security')}</Link></li>
              </ul>
            </div>
            <div className="footer-links-column">
              <h2 className="footer-links-title">{t('footer.topics_title')}</h2>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/thought-diary')}>{t('nav.thought_diary')}</Link></li>
                <li><Link to={getLocalizedLink('/anxiety')}>{t('nav.anxiety')}</Link></li>
                <li><Link to={getLocalizedLink('/burnout')}>{t('nav.burnout')}</Link></li>
                <li><Link to={getLocalizedLink('/between-sessions')}>{t('nav.between_sessions')}</Link></li>
                <li><Link to={getLocalizedLink('/self-check')}>{t('nav.self_check')}</Link></li>
              </ul>
            </div>
            <div className="footer-links-column">
              <h2 className="footer-links-title">{t('footer.docs_title')}</h2>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/impressum')}>{t('footer.impressum')}</Link></li>
                <li><Link to={getLocalizedLink('/faq')}>{t('footer.faq')}</Link></li>
                <li><Link to={getLocalizedLink('/privacy')}>{t('footer.privacy')}</Link></li>
                <li><Link to={getLocalizedLink('/terms')}>{t('footer.terms')}</Link></li>
                <li><Link to={getLocalizedLink('/cookies')}>{t('footer.cookies', 'Cookies')}</Link></li>
                <li><Link to={getLocalizedLink('/clinical-safety')}>{t('footer.clinical', 'Clinical safety')}</Link></li>
                <li><Link to={getLocalizedLink('/editorial-policy')}>{t('footer.editorial', 'Editorial policy')}</Link></li>
                <li>
                  <Link to={getLocalizedLink('/contact')}>
                    {t('footer.contact')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-social">
          <h2 className="footer-social-title">{t('footer.follow', 'Follow us')}</h2>
          <div className="social-icons">
            <a href={LUMI_INSTAGRAM_PROFILE[language] || LUMI_INSTAGRAM_PROFILE.en} target="_blank" rel="noopener noreferrer" className="social-icon instagram" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href="https://www.linkedin.com/company/aipsyhelp" target="_blank" rel="noopener noreferrer" className="social-icon linkedin" aria-label="LinkedIn">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </a>
            <a href="https://www.facebook.com/PsyHelpUA/" target="_blank" rel="noopener noreferrer" className="social-icon facebook" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-3 7h-1.924c-.615 0-1.076.252-1.076.889v1.111h3l-.238 3h-2.762v8h-3v-8h-2v-3h2v-1.923c0-2.022 1.064-3.077 3.461-3.077h2.539v3z"/></svg>
            </a>
            <a href="https://www.youtube.com/@AIPSYHELP" target="_blank" rel="noopener noreferrer" className="social-icon youtube" aria-label="YouTube">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@ai.psy.help" target="_blank" rel="noopener noreferrer" className="social-icon tiktok" aria-label="TikTok">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            </a>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="copyright">{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
